import { componentStackFor } from '../utils/caughtComponentStack';
import { ErrorBoundaryFallback } from './ErrorBoundaryFallback';
import type { ErrorComponentProps } from '@tanstack/react-router';

/**
 * The first few component frames of a React component stack, as one line.
 *
 * Three is the useful depth: the throwing component and enough of its parents
 * to place it, without a wall of providers. Frames read "    in Foo (at bar)" —
 * the name is what identifies the component, the file is noise next to it.
 */
export function topFrames(componentStack: string | undefined, limit = 3): string {
  if (!componentStack) return '';
  const names = componentStack
    .split('\n')
    .map((line) => /^\s*(?:in|at)\s+([A-Za-z0-9_$.]+)/.exec(line)?.[1])
    .filter((name): name is string => !!name && name !== 'Unknown');
  return names.slice(0, limit).join(' < ');
}

/**
 * Adapter for TanStack Router's errorComponent prop. Converts the router's
 * ErrorComponentProps into the ErrorBoundaryFallback interface.
 *
 * The component stack is the whole diagnosis for a render fault, and a route
 * error component never sees one: TanStack's CatchBoundary builds it with
 * `{ error, reset }` and drops the `info` its own types promise. So the stack
 * comes from React itself (see lib/caughtComponentStack), with `info` still
 * read first in case a future version starts passing it.
 *
 * React writes the stack to the console too, but nobody has devtools open when
 * it happens — least of all the person reporting it. So it is logged under a
 * greppable tag AND, in dev, shown on the card, where a screenshot carries it.
 *
 * Usage in route definitions:
 * ```ts
 * errorComponent: RouteErrorFallback,
 * ```
 */
export const RouteErrorFallback = ({
  error,
  info,
  reset,
}: ErrorComponentProps) => {
  const err = error instanceof Error ? error : new Error(String(error));
  const componentStack = info?.componentStack ?? componentStackFor(error);
  const where = topFrames(componentStack);
  // Not componentDidCatch — this is a function component rendered BY the
  // boundary, so the log runs on render. A repeated fault logging twice is a
  // fair price for the stack being in the console at all.
  console.error('[RouteErrorFallback]', err, componentStack ?? '');
  return (
    <ErrorBoundaryFallback
      error={err}
      detail={where}
      reset={reset}
      className="min-h-64"
    />
  );
};
